/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { IActionContext, getUrlSync } from '@msdyn365-commerce/core-internal';
import { SalesOrder, SalesStatus } from '@msdyn365-commerce/retail-proxy';
import { StringExtensions } from '@msdyn365-commerce-modules/retail-actions';

/**
 * Resources for order status.
 */
export interface IOrderHistoryWithSearchAndFiltersOrderStatusResources {
    orderStatusCreated: string;
    orderStatusDelivered: string;
    orderStatusInvoiced: string;
    orderStatusCanceled: string;
}

/**
 * Retrieves the url for order details page.
 * @param actionContext - Core action context.
 * @param salesOrder - Order information.
 * @returns Url to order details.
 */
export const getOrderDetailsPageUrl = (actionContext: IActionContext, salesOrder: SalesOrder): string => {
    const orderDetailsUrl = getUrlSync('orderDetails', actionContext) || '';
    const separator = orderDetailsUrl.includes('?') ? '&' : '?';
    if (!StringExtensions.isNullOrWhitespace(salesOrder.SalesId)) {
        // Redirect with sales order sales id
        return `${orderDetailsUrl}${separator}salesId=${salesOrder.SalesId}`;
    }

    // Redirect with sales order transaction id
    return `${orderDetailsUrl}${separator}transactionId=${salesOrder.Id}`;
};

/**
 * Retrieves the text for order status.
 * @param status - The sales order.
 * @param resources - Resources where we can find the text.
 * @returns The text for the order status.
 */
export const getOrderStatusLabel = (
    status: SalesStatus | undefined,
    resources: IOrderHistoryWithSearchAndFiltersOrderStatusResources
): string => {
    switch (status) {
        case SalesStatus.Created:
            return resources.orderStatusCreated;
        case SalesStatus.Delivered:
            return resources.orderStatusDelivered;
        case SalesStatus.Invoiced:
            return resources.orderStatusInvoiced;
        case SalesStatus.Canceled:
            return resources.orderStatusCanceled;
        default:
            return '';
    }
};
